import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import { SOCKET_URL, getUploadUrl } from '../utils/config';
import toast from 'react-hot-toast';
import { FaPaperPlane, FaUser } from 'react-icons/fa';

let socket = null;

export default function ChatBox({ activeRecipient = null, onClearRecipient = null }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const messagesEndRef = useRef(null);

  // Load chat history (strictly for the active context)
  useEffect(() => {
    if (!user) return;

    let url = '/messages';
    if (user.type === 'admin') {
      if (activeRecipient) {
        const mId = activeRecipient.member_id || activeRecipient.id;
        url += `?memberId=${encodeURIComponent(mId)}`;
      } else {
        setMessages([]);
        return;
      }
    }

    API.get(url).then(res => {
      setMessages(res.data || []);
    }).catch(() => {});
  }, [user, activeRecipient]);

  // Connect socket and join private room
  useEffect(() => {
    if (!user) return;

    socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });

    socket.on('connect', () => {
      setConnected(true);
      const userId = user.type === 'user' ? String(user.memberId || user.id) : String(user.id);
      socket.emit('join_room', {
        userId,
        role: user.type,
        name: user.name || user.username
      });
    });

    socket.on('disconnect', () => setConnected(false));

    socket.on('receive_message', (msg) => {
      // Security & Privacy filter on client side
      if (user.type === 'user') {
        const myMemberId = String(user.memberId || '');
        const myId = String(user.id || '');
        const sId = String(msg.senderId || msg.sender_id || '');
        const rId = String(msg.recipientId || msg.recipient_id || '');
        const sRole = msg.senderRole || msg.sender_role || '';
        const isForMe = (sRole === 'admin' && (rId === myMemberId || rId === myId || (rId === 'all' && (!user.status || user.status === 'approved')))) ||
                        (sRole === 'user' && (sId === myMemberId || sId === myId));
        if (!isForMe) return;
      } else if (user.type === 'admin') {
        if (!activeRecipient) return;
        const mId = String(activeRecipient.member_id || activeRecipient.id || '');
        const sId = String(msg.senderId || msg.sender_id || '');
        const rId = String(msg.recipientId || msg.recipient_id || '');
        if (mId === 'all') {
          if (rId !== 'all') return;
        } else {
          const isForActive = sId === mId || rId === mId || rId === 'all';
          if (!isForActive) return;
        }
      }

      setMessages(prev => {
        const exists = prev.some(m => m.id === msg.id);
        if (exists) return prev;
        return [...prev, msg];
      });
    });

    return () => {
      if (socket) socket.disconnect();
    };
  }, [user, activeRecipient]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeRecipient]);

  const sendMessage = () => {
    if (!input.trim()) return;

    if (user.type === 'admin' && !activeRecipient) {
      toast.error('Please select a member to send a message');
      return;
    }

    const isBroadcast = activeRecipient?.id === 'all' || activeRecipient?.member_id === 'all';
    const recipientId = user.type === 'user'
      ? 'admin'
      : (isBroadcast ? 'all' : (activeRecipient.member_id || String(activeRecipient.id)));
    const recipientName = user.type === 'user'
      ? 'Admin Team'
      : (isBroadcast ? 'All Members' : activeRecipient.name);

    const senderId = user.type === 'user'
      ? String(user.memberId || user.id)
      : String(user.id);

    const msgData = {
      senderId,
      senderName: user.name || user.username,
      senderRole: user.type,
      recipientId,
      recipientName,
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    if (socket && connected) {
      socket.emit('send_message', msgData);
      if (isBroadcast) {
        toast.success('Bulk message broadcasted to all members!');
      }
    } else {
      // Fallback REST
      API.post('/messages', { 
        content: input.trim(),
        recipientId,
        recipientName
      }).then(res => {
        setMessages(prev => [...prev, res.data]);
        if (isBroadcast) {
          toast.success('Bulk message broadcasted to all members!');
        }
      }).catch((err) => {
        toast.error(err.response?.data?.error || 'Failed to send message');
      });
    }
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const isOwnMessage = (msg) => {
    const sRole = msg.senderRole || msg.sender_role || '';
    if (user?.type === 'admin') {
      return sRole === 'admin';
    } else {
      return sRole === 'user';
    }
  };

  // Strictly filter displayed messages to prevent any leak
  const displayedMessages = messages.filter(msg => {
    const sRole = msg.senderRole || msg.sender_role || '';
    const sId = String(msg.senderId || msg.sender_id || '');
    const rId = String(msg.recipientId || msg.recipient_id || '');

    if (user?.type === 'user') {
      const myMemberId = String(user.memberId || '');
      const myId = String(user.id || '');
      const sentByMe = (sRole === 'user') && (sId === myMemberId || sId === myId);
      const sentToMe = (sRole === 'admin') && (rId === myMemberId || rId === myId || rId === 'all');
      return sentByMe || sentToMe;
    }

    if (user?.type === 'admin') {
      if (!activeRecipient) return false;
      const mId = String(activeRecipient.member_id || activeRecipient.id || '');
      if (mId === 'all') {
        return rId === 'all';
      }
      return sId === mId || rId === mId || rId === 'all';
    }

    return false;
  });

  return (
    <div className="chat-container">
      {/* Header */}
      <div className="chat-header">
        {activeRecipient?.id === 'all' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: 'linear-gradient(135deg, #15803d, #22c55e)',
              color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 20
            }}>
              📢
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
                  All Members (Broadcast)
                </span>
                <span className="badge badge-success" style={{ fontSize: 11, padding: '2px 8px' }}>
                  Bulk Message
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#16a34a', marginTop: 2 }}>
                <div className={`chat-status-dot ${connected ? '' : 'disconnected'}`} style={{ background: connected ? '#22c55e' : '#ef4444' }}></div>
                <span>{connected ? 'Online' : 'Offline'}</span>
              </div>
            </div>
          </div>
        ) : activeRecipient ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: 'linear-gradient(135deg, #15803d, #22c55e)',
              color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 16, overflow: 'hidden'
            }}>
              {activeRecipient.photo_path ? (
                <img
                  src={getUploadUrl(activeRecipient.photo_path)}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                activeRecipient.name?.slice(0, 2).toUpperCase() || <FaUser />
              )}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
                  {activeRecipient.name}
                </span>
                <span className="badge badge-success" style={{ fontSize: 11, padding: '2px 8px' }}>
                  {activeRecipient.member_id}
                </span>
                {activeRecipient.mobile_phone && (
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    📞 {activeRecipient.mobile_phone}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#16a34a', marginTop: 2 }}>
                <div className={`chat-status-dot ${connected ? '' : 'disconnected'}`} style={{ background: connected ? '#22c55e' : '#ef4444' }}></div>
                <span>{connected ? 'Online' : 'Offline'}</span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <div style={{
              width: 44, height: 44, background: 'linear-gradient(135deg, #15803d, #22c55e)',
              borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: 'white'
            }}>
              {user?.type === 'user' ? '💬' : '👤'}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
                {user?.type === 'user' ? 'Association Support & Admin Chat' : 'Member Private Conversation'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                <div className={`chat-status-dot ${connected ? '' : 'disconnected'}`} style={{ background: connected ? '#22c55e' : '#ef4444' }}></div>
                <span>
                  {connected
                    ? (user?.type === 'user' ? 'Online' : 'Select a member to chat privately')
                    : 'Connecting...'}
                </span>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            fontSize: 12,
            color: user.type === 'admin' ? '#15803d' : '#2563eb',
            background: user.type === 'admin' ? '#f0fdf4' : '#eff6ff',
            padding: '4px 10px',
            borderRadius: 20,
            border: `1px solid ${user.type === 'admin' ? '#bbf7d0' : '#bfdbfe'}`,
            fontWeight: 600
          }}>
            {user.type === 'admin' ? '🛡️ Admin' : '👤 Member'}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="chat-messages">
        {displayedMessages.length === 0 && (
          <div className="empty-state" style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div className="empty-state-icon" style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
            <h3 style={{ fontSize: 16, color: '#374151', marginBottom: 6 }}>
              {user.type === 'admin' && !activeRecipient
                ? 'Select a member to view messages'
                : (activeRecipient ? `No messages yet with ${activeRecipient.name}` : 'No messages yet')}
            </h3>
            <p style={{ fontSize: 13, color: '#9ca3af' }}>
              {user.type === 'admin' && !activeRecipient
                ? 'Choose a registered member from the directory on the left to start private messaging.'
                : 'Send a message below to start your conversation with the admin team.'}
            </p>
          </div>
        )}

        {displayedMessages.map((msg, i) => {
          const mine = isOwnMessage(msg);
          const senderName = msg.senderName || msg.sender_name;
          const senderRole = msg.senderRole || msg.sender_role;
          const content = msg.content;
          const time = formatTime(msg.created_at || msg.timestamp);

          return (
            <div key={msg.id || i} className={`message-bubble ${mine ? 'sent' : 'received'}`}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: mine ? 'flex-end' : 'flex-start', gap: 6, marginBottom: 2 }}>
                {!mine && (
                  <span className="message-sender" style={{ color: senderRole === 'admin' ? '#15803d' : '#2563eb', fontWeight: 600 }}>
                    {senderRole === 'admin' ? '🛡️ Admin' : '👤'} {senderName}
                  </span>
                )}
                {(msg.recipientId === 'all' || msg.recipient_id === 'all') && (
                  <span style={{ fontSize: 10, background: mine ? 'rgba(255,255,255,0.25)' : '#dcfce7', color: mine ? '#ffffff' : '#16a34a', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>
                    📢 Broadcast to All
                  </span>
                )}
              </div>
              <div className="message-content">{content}</div>
              <div className="message-time">{time}</div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-input-area">
        <textarea
          className="chat-input"
          placeholder={
            user?.type === 'user'
              ? 'Type your query to Admin team... (Press Enter to send)'
              : (activeRecipient?.id === 'all'
                  ? 'Type bulk message to broadcast to all members... (Press Enter to send)'
                  : (activeRecipient
                      ? `Message ${activeRecipient.name}... (Press Enter to send)`
                      : 'Select a registered member to send a message...'))
          }
          value={input}
          disabled={user?.type === 'admin' && !activeRecipient}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <button
          className="send-btn"
          onClick={sendMessage}
          disabled={!input.trim() || (user?.type === 'admin' && !activeRecipient)}
        >
          <FaPaperPlane size={15} />
        </button>
      </div>
    </div>
  );
}

